import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    ColumnDef,
    flexRender,
} from '@tanstack/react-table';
import { NSTable } from 'bricks';
import RenderIf from 'components/RenderIf/RenderIf';
import { WattsType } from 'views/ProFormaTable/constants';
import { ProFormaModelIds } from 'constants/proForma';
import { COLUMN_KEYS_BY_MODEL_ID, ColumnKeys } from '../OtherIncomeTable/constants';
import { useProFormaSocketContext } from '../../../../socketContext/ProFormaSocketProvider';
import './TotalPotentialIncomeTable.scss';

export interface ITotalPotentialIncomeTableProps {
    operationBeginIndex: number;
    baseCashFlowColumns: ColumnDef<any>[];
    isRetailView?: boolean;
}

const TotalPotentialIncomeTable = ({ operationBeginIndex, baseCashFlowColumns, isRetailView = false }: ITotalPotentialIncomeTableProps) => {
    const {
        incomeSummary, showCents, formatDisplayValue, operationsCashFlow,
        proForma, wattsType,
    } = useProFormaSocketContext();

    const totalPotentialIncomeData = isRetailView ? incomeSummary.retailTotalPotentialIncome : incomeSummary.totalPotentialIncome;
    const totalPotentialIncomeCashFlowKey = isRetailView ? 'retailTotalPotentialIncome' : 'totalPotentialIncome';

    const totalPotentialIncomeCashFlowColumns: ColumnDef<any>[] = baseCashFlowColumns.map((column, cashFlowIndex) => ({
        ...column,
        id: `total-potential-income-${column.id}`,
        footer: () => (
            <RenderIf isTrue={operationBeginIndex}>
                <span className="text-dark">
                    {formatDisplayValue((operationsCashFlow?.[cashFlowIndex]?.[totalPotentialIncomeCashFlowKey] as number) || 0, showCents, 'currency')}
                </span>
            </RenderIf>
        ),
    }));

     
    const COLUMNS_BY_KEY: { [key in ColumnKeys]: ColumnDef<any> } = {
        [ColumnKeys.START_DATE]: {
            accessorKey: 'startDate',
            id: 'startDate',
        },
        [ColumnKeys.ANNUAL_GROWTH]: {
            accessorKey: 'annualGrowth',
            id: 'annualGrowth',
        },
        [ColumnKeys.PERCENT_OF_TOTAL_RENT]: {
            accessorKey: 'percentOfTotalRent',
            id: 'percentOfTotalRent',
        },
        [ColumnKeys.PRICE_PER_SF]: {
            accessorKey: 'pricePerSf',
            id: 'pricePerSf',
            footer: () => <span>{formatDisplayValue(totalPotentialIncomeData?.totalPricePerSf || 0, true, 'currency')}</span>,
        },
        [ColumnKeys.PRICE_PER_SF_PER_YEAR]: {
            accessorKey: 'pricePerSfPerYear',
            id: 'pricePerSfPerYear',
            footer: () => <span>{formatDisplayValue(totalPotentialIncomeData?.totalPricePerSfPerYear || 0, true, 'currency')}</span>,
        },
        [ColumnKeys.PRICE_PER_UNIT]: {
            accessorKey: 'pricePerUnit',
            id: 'pricePerUnit',
            footer: () => <span>{formatDisplayValue(totalPotentialIncomeData?.totalPricePerUnit || 0, true, 'currency')}</span>,
        },
        [ColumnKeys.PRICE_PER_BED]: {
            accessorKey: 'pricePerBed',
            id: 'pricePerBed',
            footer: () => <span>{formatDisplayValue(totalPotentialIncomeData?.totalPricePerBed || 0, true, 'currency')}</span>,
        },
        [ColumnKeys.BEDS]: {
            id: 'beds',
        },
        [ColumnKeys.VALUE_PER_RACK_PER_YEAR]: {
            accessorKey: 'valuePerRackPerYear',
            id: 'valuePerRackPerYear',
            footer: () => <span>{formatDisplayValue(totalPotentialIncomeData?.totalValuePerRackPerYear || 0, true, 'currency')}</span>,
        },
        [ColumnKeys.VALUE_PER_KW_PER_YEAR]: wattsType === WattsType.MEGAWATTS ? {
            accessorKey: 'valuePerMwPerYear',
            id: 'valuePerMwPerYear',
            footer: () => <span>{formatDisplayValue(totalPotentialIncomeData?.totalValuePerMwPerYear || 0, true, 'currency')}</span>,
        } : {
            accessorKey: 'valuePerKwPerYear',
            id: 'valuePerKwPerYear',
            footer: () => <span>{formatDisplayValue(totalPotentialIncomeData?.totalValuePerKwPerYear || 0, true, 'currency')}</span>,
        },
    };

    const totalPotentialIncomeColumns: ColumnDef<any>[] = [
        {
            accessorKey: 'name',
            id: 'name',
            footer: () => <span className="px-1">Total potential income</span>,
        },
        ...(isRetailView
            ? COLUMN_KEYS_BY_MODEL_ID[ProFormaModelIds.RETAIL]
            : COLUMN_KEYS_BY_MODEL_ID[proForma.proFormaModelId]
        ).map(key => COLUMNS_BY_KEY[key]),
        {
            accessorKey: 'amountPerYear',
            id: 'amountPerYear',
            footer: () => (
                <span className="pr-1">
                    {formatDisplayValue(totalPotentialIncomeData?.totalAmountPerYear || 0, showCents, 'currency')}
                </span>
            ),
        },
        ...totalPotentialIncomeCashFlowColumns,
    ];

    const table = useReactTable({
        data: [],
        columns: totalPotentialIncomeColumns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    return (
        <NSTable className={`NSTable TotalPotentialIncomeTable--${isRetailView ? 'retail' : proForma.proFormaModelName.replace(/ /g, '-')} mb-1`}>
            <tfoot className="NSTable__tfoot">
                {table.getFooterGroups().map(footerGroup => (
                    <tr key={footerGroup.id} className="NSTable__tfoot__tr">
                        {footerGroup.headers.map(header => (
                            <th key={header.id} className="NSTable__tfoot__tr__th p-1">
                                {flexRender(
                                    header.column.columnDef.footer,
                                    header.getContext(),
                                )}
                            </th>
                        ))}
                    </tr>
                ))}
            </tfoot>
        </NSTable>
    );
};

export default TotalPotentialIncomeTable;
